import { SITE_URL, SITE_NAME, SITE_LOGO_URL } from '@/lib/seo';
import { type ArticleData } from '@/mocks/articles';

export interface SchemaObject {
  id: string;
  data: Record<string, unknown>;
}

export function organizationSchema(): SchemaObject {
  return {
    id: 'schema-organization',
    data: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: SITE_LOGO_URL,
    },
  };
}

export function websiteSchema(): SchemaObject {
  return {
    id: 'schema-website',
    data: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_URL,
      inLanguage: 'en',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${SITE_URL}/en/products/?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
  };
}

export function webPageSchema(url: string, title: string, description: string): SchemaObject {
  return {
    id: 'schema-webpage',
    data: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description,
      url,
      inLanguage: 'en',
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: SITE_URL,
      },
    },
  };
}

export function articleSchema(article: ArticleData, url: string): SchemaObject {
  return {
    id: 'schema-article',
    data: {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.metaDesc,
      image: article.image,
      url,
      datePublished: article.published,
      dateModified: article.updated,
      inLanguage: 'en',
      author: {
        '@type': 'Organization',
        name: article.author,
      },
      publisher: {
        '@type': 'Organization',
        name: SITE_NAME,
        logo: { '@type': 'ImageObject', url: SITE_LOGO_URL },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url,
      },
    },
  };
}

export function breadcrumbSchema(items: { name: string; url: string }[]): SchemaObject {
  return {
    id: 'schema-breadcrumb',
    data: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.name,
        item: item.url,
      })),
    },
  };
}

export function faqSchema(faq: { q: string; a: string }[]): SchemaObject {
  return {
    id: 'schema-faq',
    data: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  };
}